/**
 * DocumentImportDialog — single-document recognition wizard for runtime list views.
 *
 * Steps:
 *   0. Recognize (auto-runs on open) → POST /v1/import/{conn}/{obj}/recognize-document
 *      (image/PDF → OCR + AI; .xlsx/.xls → structural parse + AI)
 *   1. Review header fields + tabular part rows + raw text; Attach/Post toggles
 *   2. Commit → POST /v1/import/{conn}/{obj}/commit; show result
 *
 * Documents are single-file in v1 (spec `2026-08-11-platform-file-import-design.md`
 * §5) — unlike `ScanImportDialog`'s N-file batch, this dialog always reviews
 * exactly one record. Field-editor patterns (ref/boolean/number/date cells,
 * tabular-part grid) are intentionally mirrored from `ScanImportDialog` rather
 * than imported, since those helpers are file-private there.
 */
import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import CircularProgress from '@mui/material/CircularProgress'
import Collapse from '@mui/material/Collapse'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Alert from '@mui/material/Alert'
import FormControlLabel from '@mui/material/FormControlLabel'
import IconButton from '@mui/material/IconButton'
import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Stepper from '@mui/material/Stepper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import { DateTimeField } from '@/shared/DateTimeField'
import apiClient from '@/utils/api'
import { ApiService } from '@/utils/api'
import { RefFieldWidget } from './RefFieldWidget'
import type { AttributeDef } from '../designer/services/configObjectApi'
import type { IntakeField, IntakeTabularPart, RefCandidate } from './ScanImportDialog'
import { RESIZABLE_DIALOG_PAPER } from '@/shared/resizableDialog'

const STEPS = ['Recognize', 'Review', 'Done']

interface RecognizedDocument {
  intake_id: string
  file_name: string
  status: 'ok' | 'error' | 'ai_unavailable'
  error: string | null
  raw_text: string
  record: Record<string, unknown>
  tabular: Record<string, Record<string, unknown>[]>
  ref_candidates: Record<string, RefCandidate>
}

interface CommitResult {
  created: number
  errors: { file_index: number; error: string }[]
  warnings: { file_index: number; warning: string }[]
}

function refAttr(field: IntakeField): AttributeDef {
  return {
    name: field.name,
    synonym: field.title,
    attr_type: 'ref',
    required: field.required,
    props: { target: field.target },
  } as unknown as AttributeDef
}

function FieldCell({
  field,
  value,
  refCandidate,
  onChange,
  connectionId,
}: {
  field: IntakeField
  value: unknown
  refCandidate?: RefCandidate
  onChange: (v: unknown) => void
  connectionId: string
}) {
  if (field.type === 'ref') {
    return (
      <Box sx={{ minWidth: 180 }}>
        {refCandidate && !refCandidate.resolved_id && !value && (
          <Chip
            size="small"
            color="warning"
            label={`unmatched: ${refCandidate.text}`}
            sx={{ mb: 0.5, maxWidth: 200 }}
          />
        )}
        <RefFieldWidget
          attr={refAttr(field)}
          value={value ?? null}
          onChange={onChange}
          disabled={false}
          connectionId={connectionId}
          isRequired={field.required}
        />
      </Box>
    )
  }
  if (field.type === 'boolean') {
    return <Checkbox size="small" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
  }
  if (field.type === 'number') {
    return (
      <TextField
        size="small"
        type="number"
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}
        sx={{ minWidth: 140 }}
      />
    )
  }
  if (field.type === 'date' || field.type === 'datetime' || field.type === 'time') {
    return (
      <DateTimeField
        attrType={field.type}
        value={value}
        onChange={(next) => onChange(next)}
        size="small"
        fullWidth={false}
      />
    )
  }
  return (
    <TextField
      size="small"
      fullWidth
      value={value ?? ''}
      onChange={(e) => onChange(e.target.value || null)}
    />
  )
}

function TabularPartEditor({
  part,
  rows,
  onChange,
  connectionId,
}: {
  part: IntakeTabularPart
  rows: Record<string, unknown>[]
  onChange: (rows: Record<string, unknown>[]) => void
  connectionId: string
}) {
  const setCell = (rowIdx: number, col: string, v: unknown) => {
    const next = rows.map((r, i) => (i === rowIdx ? { ...r, [col]: v } : r))
    onChange(next)
  }
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        {part.title}
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {part.columns.map((c) => (
              <TableCell key={c.name}>{c.title}</TableCell>
            ))}
            <TableCell padding="checkbox" />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, i) => (
            <TableRow key={i}>
              {part.columns.map((c) => (
                <TableCell key={c.name}>
                  {c.type === 'ref' && typeof row[`${c.name}__text`] === 'string' && !row[c.name] && (
                    <Typography variant="caption" color="warning.main" display="block">
                      {String(row[`${c.name}__text`])}
                    </Typography>
                  )}
                  <FieldCell
                    field={c}
                    value={row[c.name]}
                    onChange={(v) => setCell(i, c.name, v)}
                    connectionId={connectionId}
                  />
                </TableCell>
              ))}
              <TableCell padding="checkbox">
                <IconButton size="small" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <Button size="small" startIcon={<AddIcon />} onClick={() => onChange([...rows, {}])}>
        Add row
      </Button>
    </Box>
  )
}

export function DocumentImportDialog({
  open,
  onClose,
  connectionId,
  objectName,
  file,
  onImported,
}: {
  open: boolean
  onClose: () => void
  connectionId: string
  objectName: string
  file: File | null
  onImported?: () => void
}) {
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fields, setFields] = useState<IntakeField[]>([])
  const [tabularParts, setTabularParts] = useState<IntakeTabularPart[]>([])
  const [intakeId, setIntakeId] = useState('')
  const [docStatus, setDocStatus] = useState<'ok' | 'error' | 'ai_unavailable'>('ok')
  const [docError, setDocError] = useState<string | null>(null)
  const [rawText, setRawText] = useState('')
  const [showRawText, setShowRawText] = useState(false)
  const [record, setRecord] = useState<Record<string, unknown>>({})
  const [tabular, setTabular] = useState<Record<string, Record<string, unknown>[]>>({})
  const [refCandidates, setRefCandidates] = useState<Record<string, RefCandidate>>({})
  const [attachSource, setAttachSource] = useState(true)
  const [post, setPost] = useState(false)
  const [result, setResult] = useState<CommitResult | null>(null)

  const handleRecognize = useCallback(
    async (f: File) => {
      setLoading(true)
      setError(null)
      try {
        const fieldsPromise = ApiService.get<{ fields: IntakeField[]; tabular_parts: IntakeTabularPart[] }>(
          `/v1/import/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/fields`,
        )
        const formData = new FormData()
        formData.append('files', f)
        const recognizePromise = apiClient.post(
          `/v1/import/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/recognize-document`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        )
        const [fieldsResp, recognizeResp] = await Promise.all([fieldsPromise, recognizePromise])
        const data = recognizeResp.data as RecognizedDocument
        setFields(fieldsResp.fields)
        setTabularParts(fieldsResp.tabular_parts || [])
        setIntakeId(data.intake_id)
        setDocStatus(data.status)
        setDocError(data.error)
        setRawText(data.raw_text || '')
        setRecord(data.record || {})
        setTabular(data.tabular || {})
        setRefCandidates(data.ref_candidates || {})
        setStep(1)
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Recognition failed')
      }
      setLoading(false)
    },
    [connectionId, objectName],
  )

  // A mounted dialog can be reopened with a different file — clear the
  // previous session's review state, then auto-run recognition (there is no
  // file-picker step: the caller already resolved exactly one file).
  useEffect(() => {
    if (!open) {
      setStep(0)
      setError(null)
      setIntakeId('')
      setDocStatus('ok')
      setDocError(null)
      setRawText('')
      setShowRawText(false)
      setRecord({})
      setTabular({})
      setRefCandidates({})
      setAttachSource(true)
      setPost(false)
      setResult(null)
      return
    }
  }, [open])

  useEffect(() => {
    if (open && file && step === 0 && !loading && !intakeId) {
      void handleRecognize(file)
    }
  }, [open, file, step, loading, intakeId, handleRecognize])

  const handleCommit = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const body = {
        intake_id: intakeId,
        records: [{ file_index: 0, record, tabular }],
        attach_source: attachSource,
        post,
      }
      const resp = await apiClient.post(
        `/v1/import/${encodeURIComponent(connectionId)}/${encodeURIComponent(objectName)}/commit`,
        body,
      )
      const data = resp.data as CommitResult
      setResult(data)
      setStep(2)
      if (data.created > 0) onImported?.()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Import failed')
    }
    setLoading(false)
  }, [intakeId, record, tabular, attachSource, post, connectionId, objectName, onImported])

  const setRecordValue = (name: string, v: unknown) => {
    setRecord((prev) => ({ ...prev, [name]: v }))
  }

  const canCommit = docStatus !== 'error' && intakeId !== ''

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: RESIZABLE_DIALOG_PAPER }}>
      <DialogTitle>Import document — {objectName}</DialogTitle>
      <DialogContent>
        <Stepper activeStep={step} sx={{ mb: 3 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {step === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress size={32} sx={{ mb: 2 }} />
            <Typography variant="body2" color="text.secondary">
              Recognizing {file?.name ?? 'document'}…
            </Typography>
          </Box>
        )}

        {step === 1 && (
          <Box>
            {docStatus === 'error' && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {docError || 'Recognition failed for this file'}
              </Alert>
            )}
            {docStatus === 'ai_unavailable' && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                AI extraction is unavailable — fields are empty. Fill them in manually below.
              </Alert>
            )}

            {docStatus !== 'error' && (
              <>
                <Table size="small" sx={{ mb: 1 }}>
                  <TableBody>
                    {fields.map((f) => (
                      <TableRow key={f.name}>
                        <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap', width: 160 }}>
                          {f.title}
                          {f.required && ' *'}
                        </TableCell>
                        <TableCell>
                          <FieldCell
                            field={f}
                            value={record[f.name]}
                            refCandidate={refCandidates[f.name]}
                            onChange={(v) => setRecordValue(f.name, v)}
                            connectionId={connectionId}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {tabularParts.map((part) => (
                  <TabularPartEditor
                    key={part.name}
                    part={part}
                    rows={(tabular[part.name] as Record<string, unknown>[]) || []}
                    onChange={(nextRows) => setTabular((prev) => ({ ...prev, [part.name]: nextRows }))}
                    connectionId={connectionId}
                  />
                ))}

                <Button
                  size="small"
                  onClick={() => setShowRawText((v) => !v)}
                  startIcon={showRawText ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                  sx={{ mb: 1 }}
                >
                  {showRawText ? 'Hide extracted text' : 'Show extracted text'}
                </Button>
                <Collapse in={showRawText}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      fontSize: 11,
                      whiteSpace: 'pre-wrap',
                      maxHeight: 200,
                      overflow: 'auto',
                      mb: 2,
                      p: 1,
                      bgcolor: 'action.hover',
                    }}
                  >
                    {rawText || 'No text extracted'}
                  </Typography>
                </Collapse>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={attachSource}
                        onChange={(e) => setAttachSource(e.target.checked)}
                      />
                    }
                    label="Attach source file to document"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox size="small" checked={post} onChange={(e) => setPost(e.target.checked)} />
                    }
                    label="Post after create"
                  />
                </Box>
              </>
            )}
          </Box>
        )}

        {step === 2 && result && (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h4" color={result.created > 0 ? 'success.main' : 'error.main'} sx={{ mb: 1 }}>
              {result.created}
            </Typography>
            <Typography variant="body1" sx={{ mb: 2 }}>
              record{result.created === 1 ? '' : 's'} created
            </Typography>
            {result.errors.length > 0 && (
              <Alert severity="error" sx={{ textAlign: 'left', mb: 1 }}>
                {result.errors.map((e, i) => (
                  <Typography key={i} variant="caption" display="block">
                    {e.error}
                  </Typography>
                ))}
              </Alert>
            )}
            {result.warnings.length > 0 && (
              <Alert severity="warning" sx={{ textAlign: 'left' }}>
                {result.warnings.map((w, i) => (
                  <Typography key={i} variant="caption" display="block">
                    {w.warning}
                  </Typography>
                ))}
              </Alert>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === 2 ? (
          <Button variant="contained" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button onClick={onClose}>Cancel</Button>
            {step === 1 && (
              <Button variant="contained" onClick={handleCommit} disabled={loading || !canCommit}>
                {loading ? <CircularProgress size={18} sx={{ mr: 1 }} /> : null}
                Create record
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  )
}
